import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ContactForm from '@/components/forms/ContactForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    fetchContact();
  }, [id]);

  const fetchContact = async () => {
    try {
      const response = await api.get(`/contacts/${id}`);
      setContact(response.data);
      document.title = `${response.data.nom_prenom} - Reno Resolve`;
    } catch (error) {
      console.error('Erreur chargement contact:', error);
      toast.error('Contact non trouvé');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce contact ?')) return;
    
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Contact supprimé');
      navigate('/contacts');
    } catch (error) {
      console.error('Erreur suppression contact:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleContactUpdated = () => {
    setShowEdit(false);
    fetchContact();
    toast.success('Contact mis à jour');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/contacts')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {contact.nom_prenom}
            </h1>
            <p className="text-slate-600 font-mono text-sm">{contact.contact_id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-contact-btn">
            <Edit2 className="w-4 h-4 mr-2" />
            Modifier
          </Button>
          <Button variant="destructive" onClick={handleDelete} data-testid="delete-contact-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Informations Générales
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Catégorie</p>
                <Badge className="mt-1">{contact.categorie || '-'}</Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Entreprise</p>
                <p className="text-slate-900 mt-1">{contact.entreprise || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Téléphone</p>
                <p className="text-slate-900 mt-1">{contact.telephone}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="text-slate-900 mt-1">{contact.email || '-'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-slate-500">Adresse</p>
                <p className="text-slate-900 mt-1">
                  {contact.adresse || '-'}
                  {contact.ville && `, ${contact.ville}`}
                  {contact.code_postal && ` (${contact.code_postal})`}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Site web</p>
                <p className="text-slate-900 mt-1">
                  {contact.site_web ? (
                    <a
                      href={contact.site_web.startsWith('http') ? contact.site_web : `https://${contact.site_web}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {contact.site_web}
                    </a>
                  ) : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Date de création</p>
                <p className="text-slate-900 mt-1">
                  {format(new Date(contact.date_creation), 'dd/MM/yyyy', { locale: fr })}
                </p>
              </div>
            </div>
          </div>

          {contact.notes && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Notes
              </h2>
              <p className="text-slate-700 whitespace-pre-wrap">{contact.notes}</p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2">Actions Rapides</h3>
            <div className="space-y-2">
              <Button className="w-full" onClick={() => navigate('/devis')} data-testid="create-devis-btn">
                Créer un Devis
              </Button>
              <Button className="w-full" variant="outline" onClick={() => navigate('/chantiers')}>
                Créer un Chantier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Contact</DialogTitle>
          </DialogHeader>
          <ContactForm
            contact={contact}
            onSuccess={handleContactUpdated}
            onCancel={() => setShowEdit(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}